// One body scroll lock, reference-counted, shared by everything that needs it.
//
// ── Why this exists ─────────────────────────────────────────────────────────
//
// `document.body.style.overflow` had two uncoordinated writers:
//
//   client/src/App.js      the mobile sidebar — sets overflow/position/width/
//                          top, and clears all four when it closes
//   client/src/ui/components/Modal.js
//                          every dialog — saved the CURRENT value on open and
//                          wrote that saved value back on close
//
// Save-and-restore is only correct if nothing else can change the value in
// between. On a phone something routinely does:
//
//   1. worker taps Menu           sidebar sets overflow:hidden
//   2. worker opens a dialog      Modal saves previousOverflow = 'hidden'
//   3. sidebar closes behind it   App.js clears overflow
//   4. worker closes the dialog   Modal writes 'hidden' BACK
//
// The body is now locked with nobody holding a lock, so nothing below the fold
// can be scrolled to. The page looks like it is missing its lower half — and
// the only thing that clears it is a resize, because App.js's effect re-runs on
// `[isMobile, sidebarOpen]` and its else branch blanks the property. Rotating
// the phone is a resize.
//
// That is the "have to rotate to landscape to see the rest of the page"
// report, and it is why it looked like a rendering fault rather than a stuck
// style: the layout is fine, the page just cannot move.
//
// Modal.js also carried a comment claiming it "counts open modals so nested
// modals don't accidentally unlock when one closes". It did not count. Two
// overlapping dialogs — a confirm raised from inside an editor — had the same
// clobber in the other direction: the first to close unlocked the page under
// the one still on screen.
//
// ── The rule this replaces both with ────────────────────────────────────────
//
// Holders are counted. The FIRST holder captures the body's inline styles and
// applies the lock; the LAST one to leave restores exactly what was captured.
// Anyone in between changes nothing. No holder can strand the page, and no
// holder can unlock it out from under another.
//
// Deliberately module scope rather than React context: the sidebar lives in
// App.js and dialogs render through a portal from anywhere beneath it, so a
// provider would have to wrap the whole tree to cover both — and a lock that
// only works when someone remembered the provider is the bug again.

/** How many holders currently want the page locked. */
let holders = 0;

/** The body's own inline styles, captured when the first holder arrived. */
let captured = null;

/**
 * Put an inline style back the way it was.
 *
 * Deliberately the CSSOM methods rather than `style.paddingRight = value`. The
 * camelCase IDL assignment is NOT reliably "unset it" for a property backed by
 * a shorthand: assigning '' to `paddingRight` leaves `padding-right` on the
 * element in jsdom's CSS engine, so the body keeps a stale offset after the
 * last holder leaves. Demonstrated, not assumed — swap this back to
 * `style[camelCase] = value` and two cases in useBodyScrollLock.test.js fail.
 *
 * `setProperty(prop, '')` would also clear it per spec; `removeProperty` for
 * the empty case just says so outright.
 */
function put(prop, value) {
  if (value) document.body.style.setProperty(prop, value);
  else document.body.style.removeProperty(prop);
}

function apply() {
  const { body, documentElement } = document;
  captured = {
    overflow: body.style.getPropertyValue('overflow'),
    paddingRight: body.style.getPropertyValue('padding-right'),
  };

  // Compensate for a disappearing scrollbar so the layout does not jump. Zero
  // on every touch device, which is where this matters most.
  const scrollbarWidth = window.innerWidth - documentElement.clientWidth;
  put('overflow', 'hidden');
  if (scrollbarWidth > 0) put('padding-right', `${scrollbarWidth}px`);
}

function restore() {
  if (!captured) return;
  put('overflow', captured.overflow);
  put('padding-right', captured.paddingRight);
  captured = null;
}

/**
 * Take a lock on body scrolling. Returns the release function.
 *
 * Releasing twice is a no-op rather than an error — a React effect cleanup can
 * legitimately run more than once in StrictMode, and double-decrementing the
 * count would unlock the page while a dialog was still on screen.
 *
 * @returns {() => void} release
 */
export function lockBodyScroll() {
  if (holders === 0) apply();
  holders += 1;

  let released = false;
  return function release() {
    if (released) return;
    released = true;
    holders -= 1;
    if (holders <= 0) {
      holders = 0;
      restore();
    }
  };
}

/** Test seam only — the count is module state and leaks between cases. */
export function __resetBodyScrollLock() {
  holders = 0;
  captured = null;
}

/** Test seam only. */
export function __bodyScrollLockHolders() {
  return holders;
}
